// TanStack Query hooks over the swappable catalog provider.
//
// The provider comes from context (useCatalogProvider), not from a module-level singleton: the
// REST provider needs the authenticated request function of @uag/react-core, which only exists
// inside the React tree - see CatalogDataProvider.tsx for the why.

import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';

import { useCatalogProvider } from './CatalogDataProvider';
import { searchModeAtom } from './state';

const FIVE_MINUTES = 5 * 60 * 1000;

// Phase 1: the STIBO export is German-only, so catalog data is fetched in 'de'. The DE/EN
// switch (catalogLocaleAtom) drives UI chrome + localized labels; product content follows
// once an English export exists. The tree itself is language-neutral (tree.json).
export const DATA_LANGUAGE = 'de';

export const useCatalogConfig = () => {
    const catalogProvider = useCatalogProvider();

    return useQuery({ queryKey: ['catalog', 'config'], queryFn: () => catalogProvider.getConfig(), staleTime: Infinity });
};

export const useDetailLayout = () => {
    const catalogProvider = useCatalogProvider();

    return useQuery({
        queryKey: ['catalog', 'detail-layout'],
        queryFn: () => catalogProvider.getDetailLayout(),
        staleTime: Infinity,
    });
};

export const useCatalogIndex = (lang: string) => {
    const catalogProvider = useCatalogProvider();

    return useQuery({
        queryKey: ['catalog', 'index', lang],
        queryFn: () => catalogProvider.getIndex(lang),
        staleTime: FIVE_MINUTES,
        // Off for the two big lists (see also useDocumentIndex): TanStack Query's default
        // structural sharing walks the OLD and the NEW result object by object on every refetch to
        // keep unchanged references stable. Across a five-figure array that costs more than the
        // re-render it saves - and every consumer here derives its own memoized structures from
        // the array anyway (see engine.ts's build*/select* functions), so a fresh array reference
        // is exactly what they expect.
        structuralSharing: false,
    });
};

export const useTree = () => {
    const catalogProvider = useCatalogProvider();

    return useQuery({
        queryKey: ['catalog', 'tree'],
        queryFn: () => catalogProvider.getTree(),
        staleTime: FIVE_MINUTES,
    });
};

export const useAttributeMapping = () => {
    const catalogProvider = useCatalogProvider();

    return useQuery({
        queryKey: ['catalog', 'attribute-mapping'],
        queryFn: () => catalogProvider.getAttributeMapping(),
        staleTime: Infinity,
    });
};

// Display-language content (attribute names, LOV code labels) - follows the DE/EN UI switch
// (catalogLocaleAtom), unlike DATA_LANGUAGE which the product data itself is pinned to.
export const useLabels = (lang: string) => {
    const catalogProvider = useCatalogProvider();

    return useQuery({
        queryKey: ['catalog', 'labels', lang],
        queryFn: () => catalogProvider.getLabels(lang),
        staleTime: Infinity,
    });
};

export const useArticle = (artNr: string | undefined) => {
    const catalogProvider = useCatalogProvider();

    return useQuery({
        queryKey: ['catalog', 'article', artNr],
        queryFn: () => catalogProvider.getArticle(artNr as string),
        enabled: !!artNr,
    });
};

export const useSystem = (nodeId: string | undefined) => {
    const catalogProvider = useCatalogProvider();

    return useQuery({
        queryKey: ['catalog', 'system', nodeId],
        queryFn: () => catalogProvider.getSystem(nodeId as string),
        enabled: !!nodeId,
    });
};

/**
 * The systems an article hangs in, fetched together - for the system documents its "Dokumente" tab
 * offers alongside the article's own.
 *
 * ONE query over N files rather than N `useSystem` calls: an article can sit under several system
 * nodes (the delivered sample goes up to 15) and the number of hooks a component renders has to
 * stay constant. The files are small (a system detail is a couple of KB), so fetching them in
 * parallel costs one round trip.
 *
 * A node without a resolvable `systems/{nodeId}.json` yields `undefined` instead of rejecting the
 * whole batch - the converter deliberately skips nodes it cannot resolve, and one missing system
 * must not blank out the documents of the others.
 */
export const useSystemsForArticle = (nodeIds: string[] | undefined) => {
    const catalogProvider = useCatalogProvider();
    // Sorted so two articles with the same systems in a different order share one cache entry.
    const keys = [...(nodeIds ?? [])].sort();

    return useQuery({
        queryKey: ['catalog', 'systems-for-article', keys],
        queryFn: () => Promise.all(keys.map((nodeId) => catalogProvider.getSystem(nodeId).catch(() => undefined))),
        enabled: keys.length > 0,
        staleTime: FIVE_MINUTES,
    });
};

// documents.json is catalog-wide (potentially many rows - one per document/owning-article
// assignment, not capped like thumbnails' one-row-per-article map), so it is fetched lazily,
// only once the user actually switches to document search - never eagerly alongside the index.
// `forceEnabled` lets a caller outside CatalogPage's own search-mode toggle (e.g. SystemDetailPage's
// "Dokumente" tab) fetch it regardless of the global searchModeAtom.
export const useDocumentIndex = (forceEnabled?: boolean) => {
    const catalogProvider = useCatalogProvider();
    const searchMode = useAtomValue(searchModeAtom);

    return useQuery({
        queryKey: ['catalog', 'documents'],
        queryFn: () => catalogProvider.getDocumentIndex(),
        staleTime: FIVE_MINUTES,
        // Same reasoning as useCatalogIndex above.
        structuralSharing: false,
        enabled: forceEnabled ?? searchMode === 'documents',
    });
};
